<script setup lang="ts">
import { computed } from 'vue'
import SparkIcon from '@/components/icons/SparkIcon.vue'
import { useSpecialCollectibleChallenge } from '@/composables/game/challenges'
import { SPECIAL_CURRENCY_ID } from '@/types/shared/player'
import CollectibleChallengeIcon from '@/assets/images/challenges/collectible/collectible-challenge-button.webp'
import HolidayOrbChallengeIcon from '@/assets/images/challenges/holiday-orb/holiday-orb-challenge-button-icon.webp'

const { countdown, formattedCountdown, isSpecialCollectibleChallengeLive, specialCollectibleChallengeName, specialCollectibleChallengeCurrency } = useSpecialCollectibleChallenge()

const isHolidayOrb = computed(() => specialCollectibleChallengeCurrency.value === SPECIAL_CURRENCY_ID.HOLIDAY_ORB)
const buttonIcon = computed(() => isHolidayOrb.value ? HolidayOrbChallengeIcon : CollectibleChallengeIcon)
</script>

<template>
  <div
    v-if="isSpecialCollectibleChallengeLive"
    class="flex flex-col items-center justify-center"
  >
    <div class="relative flex items-center justify-center size-12 rounded-[15px]">
      <SparkIcon class="absolute sparkling-star h-4 w-4 z-[10]" />
      <img
        class="size-full object-cover z-[3]"
        :src="buttonIcon"
        alt="Collectible Challenge"
      >
    </div>
    <div class="z-[10] flex flex-col -mt-2 items-center">
      <div class="z-[1] whitespace-nowrap h-[18px] px-1.5 rounded-full bg-white flex items-center justify-center text-[10px] font-bold leading-[14px] -tracking-[0.3px] text-tropee-black">
        {{ specialCollectibleChallengeName }}
      </div>
      <div
        class="z-[2] -mt-1 whitespace-nowrap h-[18px] px-1.5 rounded-full bg-white flex items-center justify-center text-[10px] font-bold leading-[14px] -tracking-[0.3px] tabular-nums"
        :class="countdown < (3600 * 24) ? 'text-red-danger' : 'text-tropee-black'"
      >
        {{ formattedCountdown }}
      </div>
    </div>
  </div>
</template>

<style scoped>
.sparkling-star {
  top: 4px;
  right: 4px;
  animation: sparkle 2.5s infinite;
  transition: all;
  transition-delay: 200ms;
}

@keyframes sparkle {
  0% {
    bottom: auto;
    left: auto;
    top: 4px;
    right: 4px;
    opacity: 1;
  }
  20% {
    bottom: auto;
    left: auto;
    top: 4px;
    right: 4px;
    opacity: 0;
  }
  35% {
    top: auto;
    right: auto;
    bottom: 4px;
    left: 4px;
    opacity: 0;
  }
  50% {
    top: auto;
    right: auto;
    bottom: 4px;
    left: 4px;
    opacity: 1;
  }
  70% {
    top: auto;
    right: auto;
    bottom: 4px;
    left: 4px;
    opacity: 0;
  }
  85% {
    bottom: auto;
    left: auto;
    top: 4px;
    right: 4px;
    opacity: 0;
  }
  100% {
    bottom: auto;
    left: auto;
    top: 4px;
    right: 4px;
    opacity: 1;
  }
}

.collectible-gradient-background {
  background: linear-gradient(180deg, #52EDCC 0.01%, #4FAF5C 23.08%, #000 36.54%)
}

.green-blur {
  background: #1AC080;
  filter: blur(9px);
}

.collectible-challenge-button::before {
  background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.4) 0%,
      rgba(255, 255, 255, 1) 50%,
      rgba(255, 255, 255, 0.4) 100%
  );
  border-radius: 15px;
  content: "";
  inset: 0;
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  padding: 2px; /* this is the border width */
  position: absolute;
  z-index: 10;
  pointer-events: none;
}
</style>